import { useDebouncedNameUniquenessChecker } from "$root/functional-areas/named-entities/uniqueness";
import { useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import type { Abortable, MaybePromise } from "@mdotm/utils/promise";
import { UserCancelledError } from "$root/utils";
import {
	type DialogBoxSize,
	type DialogProps,
	CircularProgressBar,
	Column,
	Dialog,
} from "@mdotm/shadcn/legacy/components";
import { type FormSubmitOpts, TextInput, Button, FormField, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import {
	type NodeOrFn,
	WrapIfStrOrNumOrEmpty,
	adaptAnimatedNodeProvider,
	renderNodeOrFn,
	spawn,
	useUniqueDOMId,
} from "@mdotm/shadcn/legacy/react-extensions";
import { type MaybeFn, maybeCall } from "@mdotm/shadcn/legacy/utils";
import { useState, type ReactNode } from "react";

export type EntityManagementBaseDialogCommon<T> = {
	children?: ReactNode;
	header?: string | ReactNode;
	confirmButton?: string | NodeOrFn;
	inputLabel?: string;
	placeholder?: string;
	// TODO(MDOT-24848): `opts` reaches no caller — every handler passed here takes the name alone —
	// so drop the parameter and this becomes `(name: string) => MaybePromise<T>`. Written out rather
	// than named because `FormSubmitFn` fixes its first parameter to a `FormEvent`; the alternative
	// is a variadic `FormSubmitFnWith<[string], T>` exported beside it, as the legacy type was.
	onSubmitAsync: (name: string, opts: FormSubmitOpts) => MaybePromise<T>;
	cancelButton?: string | NodeOrFn<{ onClick(): void }>;
	size?: DialogBoxSize;
	currentName?: string;
	/** Human readable name */
	entityType?: string;
	suggestedName?: string;
	checkIfNameIsAvailable?: MaybeFn<MaybePromise<boolean>, [string, Abortable | undefined]>;
};

export type SpawnEntityManagementBaseDialogParams<T> = EntityManagementBaseDialogCommon<T>;

export function spawnEntityManagementBaseDialog<T>(params: SpawnEntityManagementBaseDialogParams<T>): Promise<T> {
	const { onSubmitAsync, ...forward } = params ?? {};

	return spawn<T>(
		adaptAnimatedNodeProvider(({ resolve, reject, show, onHidden }) => (
			<EntityManagementBaseDialog
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => reject(new UserCancelledError())}
				onSubmitAsync={async (...p) => {
					resolve(await onSubmitAsync(...p));
				}}
				{...forward}
			/>
		)),
	).promise;
}

export type EntityManagementBaseDialogProps<T> = EntityManagementBaseDialogCommon<T> & {
	show: boolean;
	onClose?(): void;
	onAnimationStateChange?: DialogProps["onAnimationStateChange"];
};

export function EntityManagementBaseDialog<T>(props: EntityManagementBaseDialogProps<T>): React.JSX.Element {
	const [name, setName] = useState(props.suggestedName ?? props.currentName ?? "");
	const allNamesAreValid = !props.checkIfNameIsAvailable;
	const uniquenessQueryId = useUniqueDOMId();
	const { checkIfNameIsAvailable } = useDebouncedNameUniquenessChecker({
		currentName: props.currentName,
		isNameAvailableApi: (n, opts) => maybeCall(props.checkIfNameIsAvailable ?? true, n, opts),
	});
	const isQueryEnabled = Boolean(name && !allNamesAreValid);
	const isUniqueQuery = useQueryNoRefetch({
		enabled: isQueryEnabled,
		queryKey: ["EntityManagementBase", uniquenessQueryId, name],
		queryFn: () => checkIfNameIsAvailable(name),
	});
	const isNameAvailable = allNamesAreValid || (isUniqueQuery.data ?? true);

	return (
		<Dialog
			size={props.size ?? "small"}
			show={props.show}
			onAnimationStateChange={props.onAnimationStateChange}
			onClose={props.onClose}
			onSubmitAsync={(_, opts) => props.onSubmitAsync?.(name, opts)}
			header={props.header ?? ""}
			footer={
				<div className="flex justify-between">
					<WrapIfStrOrNumOrEmpty
						wrapper={(content) => (
							<Button
								palette="tertiary"
								size="small"
								type="button"
								onClick={() => props.onClose?.()}
								data-qualifier="EntityManagementDialog/CancelButton"
							>
								{content}
							</Button>
						)}
					>
						{renderNodeOrFn(props.cancelButton ?? "Cancel", { onClick: () => props.onClose?.() })}
					</WrapIfStrOrNumOrEmpty>
					<WrapIfStrOrNumOrEmpty
						wrapper={(content) => (
							<SubmitButton
								data-qualifier="EntityManagementDialog/ConfirmButton"
								palette="primary"
								size="small"
								disabled={(isQueryEnabled && isUniqueQuery.isLoading) || !name || !isNameAvailable}
							>
								{content}
							</SubmitButton>
						)}
					>
						{renderNodeOrFn(props.confirmButton ?? "Proceed")}
					</WrapIfStrOrNumOrEmpty>
				</div>
			}
		>
			<Column gap={8} flexGrow={0}>
				<FormField label={props.inputLabel} error={!name || isNameAvailable ? undefined : "Name not available"}>
					{(forward) => (
						<TextInput
							{...forward}
							innerRef={(e) => {
								e?.setAttribute("data-qualifier", "EntityManagementDialog/NameField");
								e?.focus({ preventScroll: true });
							}}
							placeholder={props.placeholder}
							onChangeText={setName}
							value={name}
							rightContent={
								isQueryEnabled && isUniqueQuery.isLoading ? (
									<CircularProgressBar className="w-3" value="indeterminate" />
								) : undefined
							}
						/>
					)}
				</FormField>
				{props.children}
			</Column>
		</Dialog>
	);
}
